import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchSocialListeningRunDetails,
  type SocialListeningRunDetails,
} from "src/services/SocialListeningAPIs";
import { ApiError } from "src/services/HttpUtils";
import { buildBrandListeningReport } from "../Utils/ListeningReportAdapter";
import { getStoredId, POLL_INTERVAL_MS } from "../Utils/SocialListeningConstants";

const NOT_FOUND_STATUS = 404;

export type ListeningReportError = "notFound" | "loadFailed";

interface FetchState {
  details: SocialListeningRunDetails | null;
  isLoading: boolean;
  error: ListeningReportError | null;
}

/** Loads one run and re-checks it while the agent is still working on it. */
export const useListeningReport = (runId: number | null) => {
  const [state, setState] = useState<FetchState>({
    details: null,
    isLoading: runId !== null,
    error: null,
  });
  const [requestKey, setRequestKey] = useState(0);
  const isPending = state.details?.status === "pending";

  useEffect(() => {
    const userId = getStoredId("user_id");
    if (runId === null || userId === null) {
      setState({ details: null, isLoading: false, error: runId === null ? null : "loadFailed" });
      return undefined;
    }

    let isCancelled = false;
    setState(current => ({ ...current, isLoading: current.details === null, error: null }));
    fetchSocialListeningRunDetails(userId, runId)
      .then(details => {
        if (!isCancelled) {
          setState({ details, isLoading: false, error: null });
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          const isNotFound = error instanceof ApiError && error.status === NOT_FOUND_STATUS;
          setState(current => ({
            details: current.details,
            isLoading: false,
            error: isNotFound ? "notFound" : "loadFailed",
          }));
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [requestKey, runId]);

  useEffect(() => {
    if (!isPending) {
      return undefined;
    }
    const timer = window.setTimeout(() => setRequestKey(key => key + 1), POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [isPending, state.details]);

  const retry = useCallback(() => setRequestKey(key => key + 1), []);
  const report = useMemo(() => buildBrandListeningReport(state.details), [state.details]);

  return {
    report,
    status: state.details?.status ?? null,
    analysisType: state.details?.analysis_type ?? null,
    isLoading: state.isLoading,
    error: state.error,
    retry,
  };
};
